import { useMemo } from 'react';

import useGetTransactionTable from '../../banking/hooks/transaction/useGetTransactionTable';
import useGetAccountDisplayDetailsFromGlobalState from '../../banking/hooks/useGetAccountDisplayDetailsFromGlobalState';
import type { BankAccountDisplayDetail } from '../../banking/types/BankAccount';
import type { Transaction } from '../../banking/types/Transactions';

interface UseEnrichedTransactionArgs {
    uuid: string;
    syncteraAccountId: string;
    enabled: boolean;
}

interface UseEnrichedTransactionResult {
    account: BankAccountDisplayDetail | null;
    transaction: Transaction | null;
    isLoading: boolean;
    notFound: boolean;
}

/**
 * Resolves a full {@link Transaction} (plus its display account) from a
 * `syncteraAccountId` + transaction `uuid`. Used to enrich the lossy,
 * agent-serialized transactions in Sprout chat widgets so they can open the
 * shared transaction details view. Fetches the single row by passing `uuid` to
 * the transactions-table endpoint.
 */
export const useEnrichedTransaction = ({
    uuid,
    syncteraAccountId,
    enabled,
}: UseEnrichedTransactionArgs): UseEnrichedTransactionResult => {
    const hasAccountId = enabled && syncteraAccountId !== '';

    const { account } = useGetAccountDisplayDetailsFromGlobalState(
        { syncteraAccountId },
        hasAccountId,
    );

    const bankAccountId = account?.bankAccountId;
    const fetchEnabled = hasAccountId && bankAccountId != null;

    const { data: tableData, isLoading: isLoadingTable } =
        useGetTransactionTable(
            {
                bankAccountId: bankAccountId ?? '',
                uuid,
            },
            { enabled: fetchEnabled },
        );

    const transaction = useMemo(() => {
        const candidates: Transaction[] = [
            ...(tableData?.pendingChargeSecuredAccountTransactions ?? []),
            ...(tableData?.pendingDepositoryAccountTransactions ?? []),
            ...(tableData?.postedTransactions ?? []),
        ];
        return candidates.find((candidate) => candidate.uuid === uuid) ?? null;
    }, [tableData, uuid]);

    const isLoading = hasAccountId && (account == null || isLoadingTable);

    return {
        account: account ?? null,
        transaction,
        isLoading,
        notFound: !isLoading && transaction == null,
    };
};

export default useEnrichedTransaction;
